/**
 * Role-based Access Control Configuration
 *
 * This file is the shared source of truth for:
 * - which menu sections exist
 * - which routes belong to each section
 * - which roles can access those sections
 *
 * Based on the roles table:
 * 1 - Admin: Full access to all sections
 * 2 - Operador: Vehicle operators
 * 3 - Vendedor: Sales (Ventas section)
 * 4 - Tráfico: Traffic management (Operaciones section)
 * 5 - Monitorista: Monitoring (Operaciones section)
 * 6 - Compras: Purchases (Finanzas section)
 * 7 - Finanzas: Finance (Finanzas section)
 * 8 - RRHH: Human Resources (RRHH section)
 * 9 - Facturación: Billing (Finanzas/Facturación section)
 * 10 - Taller: Workshop/Maintenance (Operaciones/Mantenimiento section)
 * 11 - Cliente: Client access
 */

const createSection = (id, title, items, options = {}) => ({
    id,
    title,
    items,
    standalone: options.standalone || false,
    routes: items.map(item => item.route),
});

export const MENU_SECTIONS = {
    OPERACIONES: createSection('operaciones', 'Operaciones', [
        { route: '/flotilla/', label: 'Flotilla', icon: 'Truck', pageKey: 'operations.fleet' },
        { route: '/calendar/', label: 'Calendario', icon: 'Calendar', pageKey: 'operations.calendar' },
        { route: '/viajes/', label: 'Programa de Viajes', icon: 'MapPin', pageKey: 'operations.trips' },
        { route: '/rutas/', label: 'Rutas', icon: 'Navigation', pageKey: 'operations.routes' },
        { route: '/operaciones/', label: 'Monitoreo', icon: 'Monitor', pageKey: 'operations.monitoring' },
        { route: '/mantenimiento/mantenimiento/', label: 'Mantenimiento', icon: 'Tool', pageKey: 'operations.maintenance' },
        { route: '/mantenimiento/fumigacion/', label: 'Fumigación', icon: 'Droplet', pageKey: 'operations.fumigation' },
        { route: '/mantenimiento/limpieza/', label: 'Limpieza', icon: 'Trash2', pageKey: 'operations.cleaning' },
    ]),
    RRHH: createSection('rrhh', 'RRHH', [
        { route: '/usuarios/', label: 'Usuarios', icon: 'User', pageKey: 'users.users' },
        { route: '/empleados/', label: 'Empleados', icon: 'UserCheck', pageKey: 'users.employees' },
        { route: '/finanzas/nomina/', label: 'Nómina/Comisiones', icon: 'DollarSign', pageKey: 'users.payroll' },
    ]),
    VENTAS: createSection('ventas', 'Ventas', [
        { route: '/clientes/', label: 'Clientes', icon: 'Users', pageKey: 'sales.clients' },
        { route: '/finanzas/pagos-del-cliente/', label: 'Cuentas por Cobrar', icon: 'CreditCard', pageKey: 'sales.client_payments' },
        { route: '/finanzas/viajes-pagados/', label: 'Viajes Pagados', icon: 'CheckCircle', pageKey: 'sales.paid_trips' },
        { route: '/finanzas/calculadora/', label: 'Calculadora', icon: 'Percent', pageKey: 'sales.calculator' },
    ]),
    FINANZAS: createSection('finanzas', 'Finanzas', [
        { route: '/finanzas/disel/', label: 'Disel y Rendimiento', icon: 'Zap', pageKey: 'finance.diesel' },
        { route: '/finanzas/tag/', label: 'Cargas de Tag', icon: 'Tag', pageKey: 'finance.tag' },
        { route: '/finanzas/dashboard/', label: 'Dashboard Financiero', icon: 'BarChart2', pageKey: 'finance.dashboard' },
    ]),
    FACTURACION: createSection(
        'facturacion',
        'Uso y Facturación',
        [{ route: '/usage/', label: 'Uso y Facturación', icon: 'BarChart2', pageKey: 'billing.usage' }],
        { standalone: true }
    ),
    CLIENTE: createSection('cliente', 'Cliente', [
        { route: '/calendario-cliente/', label: 'Calendario', icon: 'Calendar', pageKey: 'client.calendar' },
        { route: '/solicitudes-viaje/', label: 'Solicitudes de Viaje', icon: 'Send', pageKey: 'client.trip_requests' },
    ]),
};

// Map role titles to allowed menu sections
export const ROLE_PERMISSIONS = {
    'Admin': ['operaciones', 'rrhh', 'ventas', 'finanzas', 'facturacion', 'cliente'],
    'Operador': [],
    'Vendedor': ['ventas'],
    'Tráfico': ['operaciones'],
    'Monitorista': ['operaciones'],
    'Compras': ['finanzas'],
    'Finanzas': ['finanzas', 'facturacion'],
    'RRHH': ['rrhh'],
    'Facturación': ['facturacion', 'finanzas'],
    'Taller': ['operaciones'],
    'Cliente': ['cliente'],
};

export const PERMISSION_PAGES = Object.values(MENU_SECTIONS).flatMap(section =>
    section.items.map(item => ({
        pageKey: item.pageKey,
        label: item.label,
        sectionId: section.id,
        sectionTitle: section.title,
        route: item.route,
    }))
);

const PAGE_TO_SECTION = PERMISSION_PAGES.reduce((acc, page) => {
    acc[page.pageKey] = page.sectionId;
    return acc;
}, {});

const getRolesFromInput = (userOrRoles) => {
    if (Array.isArray(userOrRoles)) {
        return userOrRoles;
    }

    return userOrRoles?.roles || [];
};

const getPermissionsFromInput = (userOrRoles) => {
    if (Array.isArray(userOrRoles)) {
        return [];
    }

    return userOrRoles?.permissions || [];
};

const isAdminUser = (userOrRoles) => {
    if (!Array.isArray(userOrRoles) && userOrRoles?.is_company_admin) {
        return true;
    }

    return getRolesFromInput(userOrRoles).some(role =>
        (role.title || role) === 'Admin'
    );
};

const hasExplicitPermissions = (userOrRoles) => getPermissionsFromInput(userOrRoles).length > 0;

const getPermissionValue = (permission, action) => {
    const snakeKey = `can_${action}`;
    return Boolean(permission?.[snakeKey] || permission?.[action]);
};

export const canPageAction = (userOrRoles, pageKey, action = 'read') => {
    if (!userOrRoles || !pageKey) {
        return false;
    }

    if (isAdminUser(userOrRoles)) {
        return true;
    }

    const explicitPermissions = getPermissionsFromInput(userOrRoles);
    if (explicitPermissions.length > 0) {
        const permission = explicitPermissions.find(item =>
            (item.page_key || item.pageKey) === pageKey
        );
        return getPermissionValue(permission, action);
    }

    if (action === 'delete') {
        return false;
    }

    const sectionId = PAGE_TO_SECTION[pageKey];
    return sectionId ? hasAccessToSection(getRolesFromInput(userOrRoles), sectionId) : false;
};

export const buildDefaultPermissionsForRoles = (roles) => {
    return PERMISSION_PAGES.map(page => ({
        page_key: page.pageKey,
        can_read: hasAccessToSection(roles, page.sectionId),
        can_write: hasAccessToSection(roles, page.sectionId),
        can_delete: false,
    }));
};

/**
 * Get all allowed section IDs for a user based on their roles
 * @param {Array} userRoles - Array of role objects with 'title' property
 * @returns {Set} - Set of allowed section IDs
 */
export const getAllowedSections = (userOrRoles) => {
    if (!userOrRoles) {
        return new Set();
    }

    if (isAdminUser(userOrRoles)) {
        return new Set(getMenuSections().map(section => section.id));
    }

    if (hasExplicitPermissions(userOrRoles)) {
        return new Set(
            PERMISSION_PAGES
                .filter(page => canPageAction(userOrRoles, page.pageKey, 'read'))
                .map(page => page.sectionId)
        );
    }

    const userRoles = getRolesFromInput(userOrRoles);
    if (!userRoles || userRoles.length === 0) {
        return new Set();
    }

    const allowedSections = new Set();

    userRoles.forEach(role => {
        const roleName = role.title || role;
        const permissions = ROLE_PERMISSIONS[roleName];

        if (permissions) {
            permissions.forEach(section => allowedSections.add(section));
        }
    });

    return allowedSections;
};

/**
 * Check if a user has access to a specific section
 * @param {Array} userRoles - Array of role objects with 'title' property
 * @param {string} sectionId - The section ID to check
 * @returns {boolean} - True if user has access
 */
export const hasAccessToSection = (userRoles, sectionId) => {
    const allowedSections = getAllowedSections(userRoles);
    return allowedSections.has(sectionId);
};

export const getMenuSections = () => Object.values(MENU_SECTIONS);

/**
 * Check if a user has access to a specific route
 * @param {Array} userRoles - Array of role objects with 'title' property
 * @param {string} route - The route path to check
 * @returns {boolean} - True if user has access
 */
export const hasAccessToRoute = (userRoles, route) => {
    if (!userRoles || userRoles.length === 0) {
        return false;
    }

    // General account/help pages should be available to any authenticated user
    // regardless of operational role.
    if (
        route === '/manual' ||
        route === '/manual/' ||
        route === '/cambiar-contrasena' ||
        route === '/cambiar-contrasena/'
    ) {
        return true;
    }

    if (isAdminUser(userRoles)) {
        return true;
    }

    const allowedSections = getAllowedSections(userRoles);

    // Normalize route (handle with/without trailing slash)
    const normalizedRoute = route.endsWith('/') ? route : route + '/';
    const routeWithoutSlash = route.endsWith('/') ? route.slice(0, -1) : route;

    // Find which section this route belongs to
    for (const section of Object.values(MENU_SECTIONS)) {
        // Check both with and without trailing slash
        const routeMatch = section.routes.some(r =>
            r === route || r === normalizedRoute || r === routeWithoutSlash
        );

        if (routeMatch) {
            const item = section.items.find(r =>
                r.route === route || r.route === normalizedRoute || r.route === routeWithoutSlash
            );
            if (hasExplicitPermissions(userRoles)) {
                return canPageAction(userRoles, item?.pageKey, 'read');
            }
            return allowedSections.has(section.id);
        }
    }

    // If route is not in any section, deny access by default (more secure)
    return false;
};

/**
 * Get all allowed routes for a user based on their roles
 * @param {Array} userRoles - Array of role objects with 'title' property
 * @returns {Array} - Array of allowed route paths
 */
export const getAllowedRoutes = (userRoles) => {
    if (!userRoles || userRoles.length === 0) {
        return [];
    }

    if (isAdminUser(userRoles)) {
        return getMenuSections().flatMap(section => section.routes);
    }

    if (hasExplicitPermissions(userRoles)) {
        return PERMISSION_PAGES
            .filter(page => canPageAction(userRoles, page.pageKey, 'read'))
            .map(page => page.route);
    }

    const allowedSections = getAllowedSections(userRoles);
    const allowedRoutes = [];

    getMenuSections().forEach(section => {
        if (allowedSections.has(section.id)) {
            allowedRoutes.push(...section.routes);
        }
    });

    return allowedRoutes;
};

export default {
    MENU_SECTIONS,
    ROLE_PERMISSIONS,
    PERMISSION_PAGES,
    getMenuSections,
    getAllowedSections,
    hasAccessToSection,
    hasAccessToRoute,
    getAllowedRoutes,
    canPageAction,
    buildDefaultPermissionsForRoles,
};
